import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { AppointmentResource } from 'app/models/AppointmentResource';
import { AppointmentStatusListResource } from 'app/models/AppointmentStatusListResource';
import { AppointmentStatusResource } from 'app/models/AppointmentStatusResource';
import { AvailableAppointmentListResource } from 'app/models/AvailableAppointmentListResource';
import { AvailableAppointmentResource } from 'app/models/AvailableAppointmentResource';
import { DepartmentScheduleIntervalResponse } from 'app/models/DepartmentScheduleIntervalResponse';
import { UtilsService } from 'app/services/utils.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'vbn-add-edit-appointment',
  templateUrl: './add-edit-appointment.component.html',
  styleUrls: ['./add-edit-appointment.component.scss']
})
export class AddEditAppointmentComponent implements OnInit {

  addressParams: any;
  appointmentId: number
  errorMessage: any;
  appointment: AppointmentResource;
  userEmail: string;
  appointmentStatus: string;
  appointmentStatusListResource: AppointmentStatusListResource;
  createdDate: string;
  dateFormat: string = "yyyy-MM-dd";
  locale: string = "en-US";
  departmentScheduleInterval: DepartmentScheduleIntervalResponse;
  appointmentDate: string;
  roleId: number = +(localStorage.getItem("_roleId"));
  displayUnlockSlotsOption: boolean = false;
  disableStatusDropDown: boolean = false;
  availableAppointmentList: AvailableAppointmentListResource;
  newIntervalId: number;
  newAppointmentDate: AvailableAppointmentResource;
  tooltipOptions = {
    'placement': 'right',
  }

  constructor(private activatedRoute: ActivatedRoute,
    private utilService: UtilsService,
    private translate: TranslateService,
    private _notificationService: NotificationsService,
    private router: Router) {

    this.appointment = new AppointmentResource();
    this.appointment.appointmentStatus = new AppointmentStatusResource();
    this.userEmail = this.utilService.currentUserEmail;
    this.appointmentStatusListResource = new AppointmentStatusListResource();
    this.appointmentStatusListResource.statusList = []

    this.departmentScheduleInterval = new DepartmentScheduleIntervalResponse();

    this.availableAppointmentList = new AvailableAppointmentListResource();
    this.availableAppointmentList.availableDates = [];

    this.newAppointmentDate = new AvailableAppointmentResource();
    this.newAppointmentDate.intervals = [];
  }

  ngOnInit() {
    this.getAppointmentStatus();
    this.activatedRoute.params.subscribe((params: Params) => {
      if ('json' in params) {
        this.addressParams = JSON.parse(atob(params['json']));
        this.appointmentId = this.addressParams.appointmentId;
        this.getAppointmentById();
      }
    });
  }

  getAppointmentById() {
    this.utilService.get(`/admin/appointment/getAppointmentById/${this.appointmentId}`)
      .subscribe(
        (response: AppointmentResource) => {
          if (response.errorCode === 10926) {
            forkJoin([
              this.translate.get('Error'),
              this.translate.get('Appointment not found')
            ]).subscribe(res => {
              this._notificationService.create(res[0], res[1], 'error')
            })
          } else {
            this.appointment = response;
            this.appointmentStatus = this.appointment.appointmentStatus.value;
            this.createdDate = formatDate(this.appointment.createdDate, this.dateFormat, this.locale);
            this.disableStatusDropDown = this.appointmentStatus === 'CANCELED' || this.appointmentStatus === 'RESOLVED';
            this.displayUnlockSlotsOption = this.appointmentStatus === 'CANCELED';
            this.getAppointmentDate();
          }
        },
        error => this.errorMessage = error
      )
  }

  getAppointmentDate() {
    this.utilService.get(`/admin/department/getDepartmentScheduleInterval/${this.appointment.departmentScheduleIntervalId}`)
      .subscribe(
        (response: DepartmentScheduleIntervalResponse) => {
          if (response.errorCode === 10916) {
            forkJoin([
              this.translate.get('Error'),
              this.translate.get('Department schedule interval not found')
            ]).subscribe(res => {
              this._notificationService.create(res[0], res[1], 'error')
            })
          } else {
            this.departmentScheduleInterval = response;
            this.appointmentDate = this.departmentScheduleInterval.scheduleDay + "   " + this.departmentScheduleInterval.intervalStartHour + "  - " + this.departmentScheduleInterval.intervalEndHour
          }
        },
        error => this.errorMessage = error
      )
  }

  getAppointmentStatus() {
    this.utilService.appointmentStatus$.subscribe(
      response => { this.appointmentStatusListResource = response },
      err => this.errorMessage = err
    )
  }

  assignMe(mustAssign: boolean) {
    if (mustAssign) {
      let loggedUser = atob(localStorage.getItem('_user'));
      this.appointment.assignedUserEmail = loggedUser;
    } else {
      this.appointment.assignedUserEmail = null;
    }
  }

  edit() {
    if (this.newAppointmentDate.intervals.length > 0) { //if there is a new interval selected
      this.appointment.departmentScheduleIntervalId = this.newIntervalId
    }
    this.utilService.add('/admin/appointment/editAppointment', this.appointment)
      .subscribe(
        (response: AppointmentResource) => {
          if (response.errorCode === 10927) {
            forkJoin([
              this.translate.get('Error'),
              this.translate.get('This email can not be assign to this appointment')
            ]).subscribe(res =>
              this._notificationService.create(res[0], res[1], 'error')
            )
          } else {
            if (response.errorCode === 10916) {
              forkJoin([
                this.translate.get('Error'),
                this.translate.get('Department schedule interval not found')
              ]).subscribe(res =>
                this._notificationService.create(res[0], res[1], 'error')
              )
            } else if (response.errorCode === 10930) {
              forkJoin([
                this.translate.get('Error'),
                this.translate.get('Cannot reschedule on status canceled')
              ]).subscribe(res =>
                this._notificationService.create(res[0], res[1], 'error')
              )
            } else {
              forkJoin([
                this.translate.get('Success'),
                this.translate.get('Appointment was updated')
              ]).subscribe(res =>
                this._notificationService.create(res[0], res[1], 'success')
              )
              this.appointment = response;
              this.router.navigate(['/appointments-list/' + btoa('{"departmentID":"' + this.appointment.departmentId + '"}')]);
            }
          }
        },
        error => this.errorMessage = error
      )
  }

  back() {
    this.router.navigate(['/appointments-list/'+ btoa('{"departmentID":"' + this.appointment.departmentId + '"}')])
  }

  onStatusSelect() {
    if (this.appointmentStatus === "CANCELED") {
      this.displayUnlockSlotsOption = true;
    } else {
      this.displayUnlockSlotsOption = false;
    }
    this.appointment.appointmentStatus = { ...this.appointmentStatusListResource.statusList.filter(x => x.value === this.appointmentStatus)[0] };
  }

  reschedule() {
    this.utilService.get(`/admin/appointment/getAvailableDays?departmentId=${this.appointment.departmentId}`)
      .subscribe(
        (response: AvailableAppointmentListResource) => {
          if (response.errorCode === 10903) {
            forkJoin([
              this.translate.get('Error'),
              this.translate.get('The department is no longer active'),
            ])
              .subscribe(res =>
                this._notificationService.create(res[0], res[1], 'error'));
          } else {
            if (response.errorCode === 10901) {
              forkJoin([
                this.translate.get('Error'),
                this.translate.get('Department not found'),
              ])
                .subscribe(res =>
                  this._notificationService.create(res[0], res[1], 'error'));
            } else {
              this.availableAppointmentList = response;
            }
          }
        },
        error => this.errorMessage = error
      )
  }

  selectNewInterval(id) {
    this.newIntervalId = id;
    this.newAppointmentDate = new AvailableAppointmentResource();
    this.newAppointmentDate.intervals = [];
    let found = false;
    for (let i = 0; i <= this.availableAppointmentList.availableDates.length; i++) {
      for (let j = 0; j < this.availableAppointmentList.availableDates[i].intervals.length; j++) {
        if (this.availableAppointmentList.availableDates[i].intervals[j].departmentScheduleIntervalId === this.newIntervalId) {
          this.newAppointmentDate.intervals.push({ ...this.availableAppointmentList.availableDates[i].intervals[j] })
          found = true;
          break;
        }
      }
      if (found) {
        this.newAppointmentDate.date = this.availableAppointmentList.availableDates[i].date;
        break
      }
    }

  }

  clearSelectionNewAppointment() {
    this.newIntervalId = undefined;
    this.newAppointmentDate = new AvailableAppointmentResource();
    this.newAppointmentDate.intervals = [];
  }
}
